import type { ShowTopStats } from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import type { PpaChartKpi, PpaChartResponse, PpaChartResponseBody } from './pricePackArchitecture.types';

const VALUE_SALES_SHARE_KPI_IDS = ['value_sales_share'];
const VOLUME_SALES_SHARE_KPI_IDS = ['volume_sales_share'];

const asResponseBody = (
    response: PpaChartResponse | null | undefined,
): PpaChartResponseBody | null => {
    if (!response || Array.isArray(response)) return null;
    return response;
};

const hasKpiMatch = (kpis: PpaChartKpi[] | undefined, kpiIds: string | string[]) =>
    kpis?.some((entry) => kpiIds.includes(entry.kpi_id ?? '')) ?? false;

const getKpiRawValue = (kpis: PpaChartKpi[] | undefined, kpiIds: string | string[]): number => {
    const kpi = kpis?.find((entry) => kpiIds.includes(entry.kpi_id ?? ''));
    if (!kpi) return 0;

    if (typeof kpi.raw_value === 'number' && Number.isFinite(kpi.raw_value)) {
        return kpi.raw_value;
    }

    const parsed = Number(kpi.value);
    return Number.isFinite(parsed) ? parsed : 0;
};

/** Reads show-top share KPIs from a KPI list (PA-07 axis preview). */
export const extractShowTopStatsFromKpis = (
    kpis: PpaChartKpi[] | null | undefined,
): ShowTopStats | null => {
    if (!kpis?.length) return null;

    const hasValueShare = hasKpiMatch(kpis, VALUE_SALES_SHARE_KPI_IDS);
    const hasVolumeShare = hasKpiMatch(kpis, VOLUME_SALES_SHARE_KPI_IDS);
    if (!hasValueShare && !hasVolumeShare) return null;

    return {
        valueSalesPercent: hasValueShare ? getKpiRawValue(kpis, VALUE_SALES_SHARE_KPI_IDS) : 0,
        volumeSalesPercent: hasVolumeShare ? getKpiRawValue(kpis, VOLUME_SALES_SHARE_KPI_IDS) : 0,
    };
};

/** Reads show-top share KPIs from the chart API response (PA-07 axis preview). */
export const extractShowTopStatsFromChartResponse = (
    response: PpaChartResponse | null | undefined,
): ShowTopStats | null => {
    const body = asResponseBody(response);
    return extractShowTopStatsFromKpis(body?.kpis);
};
